import React, { useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import { IGetUserOrdersQueryVariables } from 'generated/graphql-gateway';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { LoyaltyReward } from 'state/loyalty/types';

import { useEnableUnifiedMenu } from '../use-enable-unified-menu';
import useFoodware from '../use-foodware';
import { useLoyaltyRewardsList } from '../use-loyalty-rewards-list';
import { useUserOrders } from '../use-user-orders';

import { makeOrderReorderable } from './utils';

export const useReorderableOrders = (
  variables: IGetUserOrdersQueryVariables = {},
  fetchPolicy: WatchQueryFetchPolicy = 'no-cache'
) => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { allRewards, loading: rewardsLoading } = useLoyaltyRewardsList();

  const allRewardsMap = useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const {
    orders,
    error,
    loading,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    isHydratingOrder,
  } = useUserOrders(variables, fetchPolicy);

  const isLoading = loading || isHydratingOrder || rewardsLoading;

  const reorderableOrders = React.useMemo(() => {
    if (isLoading) {
      return [];
    }
    return orders.reduce((ordersAccum: IHistoricalOrder[], order) => {
      const reorderableOrder = makeOrderReorderable({
        order,
        sanityIdsExcludedFromReordering,
        isFoodware,
        allRewardsMap,
        isUnifiedMenuEnabled,
      });

      if (reorderableOrder.cart.reorderCartEntries?.length) {
        ordersAccum.push(reorderableOrder as IHistoricalOrder);
      }

      return ordersAccum;
    }, []);
  }, [
    allRewardsMap,
    isFoodware,
    isLoading,
    isUnifiedMenuEnabled,
    orders,
    sanityIdsExcludedFromReordering,
  ]);

  return {
    error,
    isLoading,
    orders,
    reorderableOrders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    isHydratingOrder,
  };
};
